import React, { useState, useEffect } from 'react';
import { Box, Typography, Slider, Paper } from '@mui/material';
import { getStateBoundary } from '../../data/censusBoundaries';

interface MapCalibrationDemoProps {
  visitedStates?: string[];
  stateStats?: { [state: string]: number };
  width?: number;
  height?: number;
}

const MapCalibrationDemo: React.FC<MapCalibrationDemoProps> = ({
  visitedStates = [
    // All 48 continental US states for alignment testing
    'Alabama', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ],
  stateStats = {},
  width = 800,
  height = 600
}) => {
  // Slider state for all parameters - start with current live values
  const [horizontalScale, setHorizontalScale] = useState(1.7);
  const [horizontalCenter, setHorizontalCenter] = useState(75);
  const [horizontalShift, setHorizontalShift] = useState(0);
  const [verticalScale, setVerticalScale] = useState(1.05);
  const [verticalCenter, setVerticalCenter] = useState(50);
  const [verticalShift, setVerticalShift] = useState(-2);
  
  const [baseMapUrl, setBaseMapUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getColorForMemoryCount = (count: number): string => {
    if (count > 10) return '#f74545'; // red
    if (count > 5) return '#ff8c00';  // orange
    return '#22c55e';                 // green
  };

  // Generate different colors for all states for better visibility
  const getStateColor = (stateName: string, index: number): string => {
    const colors = [
      '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd', '#98d8c8',
      '#f7dc6f', '#bb8fce', '#85c1e9', '#f8c471', '#82e0aa', '#f1948a', '#85c1e9',
      '#f4d03f', '#a9dfbf', '#d7bde2', '#a3e4d7', '#f9e79f', '#d5a6bd', '#aed6f1',
      '#f8d7da', '#d1ecf1', '#fff3cd', '#d4edda', '#e2e3e5', '#cce5df', '#e1bee7',
      '#ffccbc', '#c8e6c9', '#b3e5fc', '#ffe0b2', '#f8bbd9', '#dcedc1', '#ffcdd2',
      '#e6ee9c', '#ce93d8', '#80cbc4', '#ffcc80', '#c5e1a5', '#b39ddb', '#a5d6a7',
      '#90caf9', '#fff59d', '#bcaaa4', '#eeeeee', '#b0bec5', '#ffab91'
    ];
    return colors[index % colors.length];
  };

  useEffect(() => {
    const generateMap = () => {
      try {
        const mapboxToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
        
        if (!mapboxToken) {
          setError('Mapbox token not found');
          setIsLoading(false);
          return;
        }

        // Create a base US map - zoom out more to fully include Maine
        const baseMapUrl = `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/` +
          `-98.5795,39.8283,3.0,0/${width}x${height}@2x?access_token=${mapboxToken}`;
        
        setBaseMapUrl(baseMapUrl);
        setIsLoading(false);

      } catch (err) {
        console.error('Error generating map:', err);
        setError('Failed to generate map');
        setIsLoading(false);
      }
    };

    generateMap();
  }, [width, height]);

  const latLngToPercent = (lat: number, lng: number) => {
    // Map bounds
    const mapBounds = {
      north: 56.0,
      south: 24.0,
      west: -150.0,
      east: -65.0
    };
    
    // Calculate coordinates normally
    let x = ((lng - mapBounds.west) / (mapBounds.east - mapBounds.west)) * 100;
    let y = ((mapBounds.north - lat) / (mapBounds.north - mapBounds.south)) * 100;
    
    // Apply scaling with slider values
    x = (x - horizontalCenter) * horizontalScale + horizontalCenter;
    y = (y - verticalCenter) * verticalScale + verticalCenter;
    
    // Apply shifts
    x = x + horizontalShift;
    y = y + verticalShift;
    
    return { x, y };
  };

  const getStateBoundaryCoords = (stateName: string) => {
    // Use real Census Bureau boundary data - exactly like StateOverlayMap
    const stateData = getStateBoundary(stateName);
    if (!stateData || !stateData.coordinates) {
      console.warn(`No Census boundary data found for ${stateName}`);
      return [];
    }
    
    // Census data is in [lng, lat] format, convert to [lat, lng] for our use
    return stateData.coordinates.map(coord => [coord[1], coord[0]]);
  };

  const getStateBoundaryPath = (stateName: string): string => {
    const stateCoords = getStateBoundaryCoords(stateName);
    if (!stateCoords || stateCoords.length === 0) {
      console.warn(`No coordinates found for state: ${stateName}`);
      return '';
    }
    
    console.log(`Processing ${stateName} with ${stateCoords.length} coordinates`);
    
    let coords = stateCoords;
    if (coords.length > 800) {
      const step = Math.ceil(coords.length / 600);
      coords = coords.filter((_, index) => index % step === 0);
      if (coords[coords.length - 1] !== stateCoords[stateCoords.length - 1]) {
        coords.push(stateCoords[stateCoords.length - 1]);
      }
    }
    
    let path = '';
    coords.forEach((coord, index) => {
      // coord is [lat, lng] from our converted data - same as StateOverlayMap
      const { x, y } = latLngToPercent(coord[0], coord[1]);
      
      if (index === 0) {
        path += `M ${x} ${y}`;
      } else {
        path += ` L ${x} ${y}`;
      }
    });
    
    const finalPath = path + ' Z';
    console.log(`Generated path for ${stateName}: ${finalPath.substring(0, 100)}...`);
    return finalPath;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading map calibration demo...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Map Calibration Demo
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Controls Panel */}
        <Box sx={{ flex: '0 0 350px' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Calibration Controls
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Horizontal Scale: {horizontalScale.toFixed(2)}
              </Typography>
              <Slider
                value={horizontalScale}
                onChange={(_, value) => setHorizontalScale(value as number)}
                min={0.1}
                max={2.5}
                step={0.01}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Horizontal Center: {horizontalCenter}%
              </Typography>
              <Slider
                value={horizontalCenter}
                onChange={(_, value) => setHorizontalCenter(value as number)}
                min={0}
                max={100}
                step={0.01}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Horizontal Shift: {horizontalShift}%
              </Typography>
              <Slider
                value={horizontalShift}
                onChange={(_, value) => setHorizontalShift(value as number)}
                min={-10}
                max={10}
                step={0.01}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Vertical Scale: {verticalScale.toFixed(2)}
              </Typography>
              <Slider
                value={verticalScale}
                onChange={(_, value) => setVerticalScale(value as number)}
                min={0.1}
                max={1.5}
                step={0.01}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Vertical Center: {verticalCenter}%
              </Typography>
              <Slider
                value={verticalCenter}
                onChange={(_, value) => setVerticalCenter(value as number)}
                min={0}
                max={100}
                step={0.01}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Vertical Shift: {verticalShift}%
              </Typography>
              <Slider
                value={verticalShift}
                onChange={(_, value) => setVerticalShift(value as number)}
                min={-10}
                max={10}
                step={0.01}
                valueLabelDisplay="auto"
              />
            </Box>

            <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.100' }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Parameters:
              </Typography>
              <Typography variant="body2" component="div">
                x = (x - {horizontalCenter}) * {horizontalScale} + {horizontalCenter}<br/>
                y = (y - {verticalCenter}) * {verticalScale} + {verticalCenter}<br/>
                x = x + ({horizontalShift})<br/>
                y = y + ({verticalShift})
              </Typography>
            </Paper>
          </Paper>
        </Box>

        {/* Map Display */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ position: 'relative', width: width, height: height, margin: '0 auto' }}>
              {/* Base map */}
              <Box
                component="img"
                src={baseMapUrl}
                alt="US Map"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 1,
                }}
              />
              
              {/* State overlays */}
              <Box
                component="svg"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {visitedStates.map((stateName, index) => {
                  const path = getStateBoundaryPath(stateName);
                  const fillColor = getStateColor(stateName, index);
                  
                  return (
                    <path
                      key={stateName}
                      d={path}
                      fill={fillColor}
                      fillOpacity={0.4}
                      stroke={fillColor}
                      strokeWidth={0.05}
                      strokeOpacity={0.8}
                    />
                  );
                })}
              </Box>
            </Box>
            
            <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
              Showing all 48 continental US states for alignment calibration
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default MapCalibrationDemo;